import React, { useState, useRef, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Menu,
  X,
  Home,
  Package,
  Settings,
  Users,
  Award,
  ShoppingCart,
  UserCheck,
  ShoppingBag,
  Layers,
  Factory,
  DollarSign,
  BarChart3,
  Globe,
  Shield,
  User,
  LogOut,
  ChevronDown,
  Calculator,
  Tag,
  ClipboardList,
  Calendar,
  BoxIcon,
  MapPin,
  LocateIcon,
  Fingerprint,
  FileText,
  Truck,
  Warehouse
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { canCreate as canCreateFn, canView as canViewFn } from '../utils/permissions';
import { isMachineViewer } from '../utils/homeRoute';
import ConnectionStatus from './ConnectionStatus';
import MachineCompanySwitcher from './MachineCompanySwitcher';
import ResellerCompanySwitcher from './ResellerCompanySwitcher';
import ResellerCompanyFilter from './ResellerCompanyFilter';
import SuperAdminCompanySwitcher from './SuperAdminCompanySwitcher';
import Tooltip from './Tooltip';
import apiService from '../services/api';

interface RolePermission {
  id: number;
  moduleId: number;
  canView: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  module: {
    id: number;
    name: string;
    displayName: string;
    route: string;
    icon: string;
  };
}

interface UserRole {
  id: number;
  name: string;
  description: string;
  permissions: RolePermission[];
}

interface LayoutUser {
  id: number;
  name: string;
  email: string;
  companyName?: string | null;
  role: string;
  hasWaterJacket?: boolean;
  hasRapierJacket?: boolean;
  isReseller?: boolean;
  userRole?: UserRole;
  permissions?: RolePermission[];
  employee?: {
    id: number;
    canApproveAttendance?: boolean;
  };
}

interface NavigationItem {
  name: string;
  href: string;
  icon: any;
  module: string;
  children?: NavigationItem[];
}

const Layout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('sidebarCollapsed') === 'true');
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const profileDropdownRef = useRef<HTMLDivElement>(null);
  const { user, logout } = useAuth() as { user: LayoutUser | null; logout: () => void };
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // Machine-viewer logins have a single page, so the header drops the sidebar toggle
  // and back button — leaving just the title, company switcher, and profile.
  const machineViewer = isMachineViewer(user);

  // Check if we're on the machines page or rapier-machine page - keep sidebar always collapsed
  const isMachinesPage = location.pathname === '/machines' || location.pathname === '/rapier-machine';

  // Auto-collapse sidebar when navigating to machines/rapier-machine page (user can still expand via toggle)
  useEffect(() => {
    if (isMachinesPage) {
      setSidebarCollapsed(true);
    }
  }, [isMachinesPage]);

  // Open the native picker when any date/month/time input is clicked
  // (not just its calendar icon). Applies globally.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target || target.tagName !== 'INPUT') return;
      const type = (target as HTMLInputElement).type;
      if (type !== 'date' && type !== 'month' && type !== 'time' && type !== 'datetime-local' && type !== 'week') return;
      const input = target as HTMLInputElement & { showPicker?: () => void };
      if (typeof input.showPicker === 'function' && !input.disabled && !input.readOnly) {
        try { input.showPicker(); } catch { /* showPicker throws if not user-activated; safe to ignore */ }
      }
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, []);

  // Fetch modules for SUPERADMIN
  const { data: superAdminModules, isLoading: modulesLoading, error: modulesError, refetch } = useQuery({
    queryKey: ['super-admin-modules'],
    queryFn: () => apiService.get('/admin/modules/all'),
    enabled: user?.role === 'SUPERADMIN',
    staleTime: 0, // Always refetch
    refetchOnMount: true
  });

  // Fetch GPS trackers for submenu
  const { data: trackersData } = useQuery({
    queryKey: ['gps-trackers'],
    queryFn: () => apiService.get('/gps/trackers'),
    enabled: !!user, // Only fetch if user is logged in
    staleTime: 30000, // Consider data stale after 30 seconds
  });

  const trackers = Array.isArray(trackersData) ? trackersData : [];

  // Debug logging for SUPERADMIN
  useEffect(() => {
    if (user?.role === 'SUPERADMIN') {
      console.log('SUPERADMIN Debug:');
      console.log('- superAdminModules:', superAdminModules);
      console.log('- modulesLoading:', modulesLoading);
      console.log('- modulesError:', modulesError);
    }
  }, [superAdminModules, modulesLoading, modulesError, user?.role]);

  // Force refetch for SUPERADMIN on mount
  useEffect(() => {
    if (user?.role === 'SUPERADMIN') {
      refetch();
    }
  }, [user?.role, refetch]);


  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target as Node)) {
        setProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Helper function to get other accessible modules
  const getOtherAccessibleModules = (): NavigationItem[] => {
    if (!user) return [];

    // For SUPERADMIN, return modules from backend API
    if (user.role === 'SUPERADMIN') {
      console.log('getOtherAccessibleModules - SUPERADMIN path');
      console.log('- superAdminModules:', superAdminModules);
      if (!superAdminModules || !('modules' in superAdminModules) || !superAdminModules.modules) {
        console.log('No modules data available, returning empty array');
        return [];
      }
      console.log('Modules found:', (superAdminModules as any).modules.length);

      const iconMap: Record<string, any> = {
        'chip-management': Package,
        'gps-management': Settings,
        'cost-calculating': Calculator,
        'stock': Package,
        'yarn-outward': Truck,
        'yarns': Package,
        'machines': Factory,
        'employees': Users,
        'qualities': Award,
        'purchases': ShoppingCart,
        'purchase-categories': Tag,
        'clients': UserCheck,
        'sales': ShoppingBag,
        'beams': Layers,
        'production': BarChart3,
        'salary': DollarSign,
        'role-management': Shield,
      };

      // Map module names to translation keys
      const nameMap: Record<string, string> = {
        'chip-management': t('navigation.chipManagement'),
        'gps-management': t('navigation.gpsManagement'),
        'cost-calculating': 'Cost Calculating',
        'yarns': t('navigation.yarns'),
        'machines': t('navigation.machines'),
        'employees': t('navigation.employees'),
        'qualities': t('navigation.qualities'),
        'purchases': t('navigation.purchases'),
        'purchase-categories': 'Purchase Categories',
        'clients': t('navigation.clients'),
        'sales': t('navigation.sales'),
        'beams': t('navigation.beams'),
        'production': t('navigation.production'),
        'salary': t('navigation.salary'),
        'stock': t('navigation.stock'),
        'role-management': 'Role Management',
      };

      return (superAdminModules as any).modules
        .filter((module: any) => module.name !== 'company-management' && module.name !== 'yarn-outward-approve') // Hide Company Management + the removed approve module
        .map((module: any) => ({
          name: nameMap[module.name] || module.displayName,
          href: module.route,
          icon: iconMap[module.name] || Package,
          module: module.name
        }));
    }

    // Get modules from role permissions first, then fallback to direct permissions
    let accessibleModules: NavigationItem[] = [];

    // Check role permissions
    if (user.userRole?.permissions) {
      accessibleModules = user.userRole.permissions
        .filter((permission: RolePermission) => permission.canView)
        .filter((permission: RolePermission) => permission.module.name !== 'company-management' && permission.module.name !== 'yarn-outward-approve') // Hide Company Management + the removed approve module
        .map((permission: RolePermission) => {
          const module = permission.module;
          const iconMap: Record<string, any> = {
            'dashboard': BarChart3,
            'chip-management': Package,
            'gps-management': Settings,
            'cost-calculating': Calculator,
            'stock': Package,
        'yarn-outward': Truck,
            'yarns': Package,
            'machines': Factory,
            'employees': Users,
            'qualities': Award,
            'purchases': ShoppingCart,
            'purchase-categories': Tag,
            'clients': UserCheck,
            'sales': ShoppingBag,
            'beams': Layers,
            'production': BarChart3,
            'salary': DollarSign,
            'role-management': Shield,
          };

          // Map module names to translation keys
          const nameMap: Record<string, string> = {
            'chip-management': t('navigation.chipManagement'),
            'gps-management': t('navigation.gpsManagement'),
            'cost-calculating': 'Cost Calculating',
            'yarns': t('navigation.yarns'),
            'machines': t('navigation.machines'),
            'employees': t('navigation.employees'),
            'qualities': t('navigation.qualities'),
            'purchases': t('navigation.purchases'),
            'purchase-categories': 'Purchase Categories',
            'clients': t('navigation.clients'),
            'sales': t('navigation.sales'),
            'beams': t('navigation.beams'),
            'production': t('navigation.production'),
            'salary': t('navigation.salary'),
            'stock': t('navigation.stock'),
            'reports': t('navigation.reports'),
            'settings': t('navigation.settings'),
            'role-management': 'Role Management',
          };

          return {
            name: nameMap[module.name] || module.displayName,
            href: module.route,
            icon: iconMap[module.name] || Shield,
            module: module.name
          };
        });
    }

    // If no role permissions, fallback to direct permissions
    if (accessibleModules.length === 0 && user.permissions) {
      accessibleModules = user.permissions
        .filter((permission: RolePermission) => permission.canView)
        .filter((permission: RolePermission) => permission.module.name !== 'company-management' && permission.module.name !== 'yarn-outward-approve') // Hide Company Management + the removed approve module
        .map((permission: RolePermission) => {
          const module = permission.module;
          const iconMap: Record<string, any> = {
            'dashboard': BarChart3,
            'chip-management': Package,
            'gps-management': LocateIcon,
            'cost-calculating': Calculator,
            'stock': Package,
        'yarn-outward': Truck,
            'yarns': Package,
            'machines': Factory,
            'employees': Users,
            'qualities': Award,
            'purchases': ShoppingCart,
            'purchase-categories': Tag,
            'clients': UserCheck,
            'sales': ShoppingBag,
            'beams': Layers,
            'production': BarChart3,
            'salary': DollarSign,
            'role-management': Shield,
            "fingerprint-management": Fingerprint,
          };

          // Map module names to translation keys
          const nameMap: Record<string, string> = {
            'chip-management': t('navigation.chipManagement'),
            'gps-management': t('navigation.gpsManagement'),
            'cost-calculating': 'Cost Calculating',
            'yarns': t('navigation.yarns'),
            'machines': t('navigation.machines'),
            'employees': t('navigation.employees'),
            'qualities': t('navigation.qualities'),
            'purchases': t('navigation.purchases'),
            'purchase-categories': 'Purchase Categories',
            'clients': t('navigation.clients'),
            'sales': t('navigation.sales'),
            'beams': t('navigation.beams'),
            'production': t('navigation.production'),
            'salary': t('navigation.salary'),
            'stock': t('navigation.stock'),
            'reports': t('navigation.reports'),
            'settings': t('navigation.settings'),
            'role-management': 'Role Management',
            'fingerprint': 'Fingerprint',
          };

          const navItem: NavigationItem = {
            name: nameMap[module.name] || module.displayName,
            href: module.route,
            icon: iconMap[module.name] || Settings,
            module: module.name
          };

          // No submenu needed for purchases - categories managed within purchases page

          return navItem;
        });
    }

    // Create-only taka users have canCreate but not canView, so the canView filters above
    // skip taka-inventory. Surface it so they can reach the entry form from the sidebar.
    // The `!canView` guard keeps this scoped to genuine create-only users — admins/viewers
    // (canView true) are unaffected and keep their normal menu.
    if (canCreateFn(user, 'taka-inventory') && !canViewFn(user, 'taka-inventory')) {
      accessibleModules.push({ name: 'Taka Inventory', href: '/taka-inventory', icon: BoxIcon, module: 'taka-inventory' });
    }

    if (user.isReseller) {
      accessibleModules.push({ name: 'Company Management', href: '/company-management', icon: Shield, module: 'company-management' });
    }

    return accessibleModules;
  };

  // Helper function to sort modules in logical workflow order
  const sortModules = (modules: NavigationItem[]): NavigationItem[] => {
    const priorityOrder: Record<string, number> = {
      'dashboard': -1,
      'company-management': 0,
      'machines': 1,
      'gps-management': 2,
      'challan': 3,
      'bill': 4,
      'stock': 5,
      'qualities': 6,
      'clients': 7,
      'production': 8,
      'employees': 8,
      'salary': 9,
      'purchases': 10,
      'sales': 11,
      'fingerprint': 12,
      'role-management': 13,
      'settings': 14,
    };

    return modules.sort((a, b) => {
      const aPriority = priorityOrder[a.module] ?? 100;
      const bPriority = priorityOrder[b.module] ?? 100;

      if (aPriority !== bPriority) return aPriority - bPriority;
      return a.name.localeCompare(b.name);
    });
  };

  // Helper function to enhance modules with submenus
  const enhanceModulesWithSubmenus = (modules: NavigationItem[]): NavigationItem[] => {
    const result: NavigationItem[] = [];
    const accessible = new Set(modules.map(m => m.module));
    // These modules are all folded into one "Inventory" submenu, each child gated by its own module.
    const INVENTORY_MODULES = new Set(['stock', 'yarn-inward', 'yarn-dashboard', 'yarn-outward', 'taka-inventory', 'beam-master']);
    let inventoryBuilt = false;
    const buildInventory = () => {
      if (accessible.has('stock')) {
        result.push({ name: 'Challan', href: '/challan', icon: Truck, module: 'challan' });
        result.push({ name: 'Bill', href: '/bill', icon: FileText, module: 'bill' });
      }
      const children: NavigationItem[] = [];
      if (accessible.has('taka-inventory')) children.push({ name: 'Taka Inventory', href: '/taka-inventory', icon: BoxIcon, module: 'taka-inventory' });
      if (accessible.has('beam-master')) children.push({ name: 'Beam Master', href: '/beam-master', icon: BoxIcon, module: 'beam-master' });
      if (accessible.has('yarn-dashboard')) children.push({ name: 'Yarn Dashboard', href: '/yarn-dashboard', icon: BarChart3, module: 'yarn-dashboard' });
      if (accessible.has('yarn-inward')) children.push({ name: 'Yarn Inward', href: '/yarn-master', icon: BoxIcon, module: 'yarn-inward' });
      if (accessible.has('yarn-outward')) children.push({ name: 'Yarn Outward', href: '/yarn-outward', icon: Truck, module: 'yarn-outward' });
      if (children.length) result.push({ name: 'Inventory', href: children[0].href, icon: Warehouse, module: 'stock', children });
    };

    for (const module of modules) {
      if (module.module === 'machines') {
        const isSuperAdmin = user?.role === 'SUPERADMIN';
        const showWater = isSuperAdmin || user?.hasWaterJacket;
        const showRapier = isSuperAdmin || user?.hasRapierJacket;

        if (showWater) {
          result.push({ ...module, name: showRapier ? 'Water Jacket' : module.name, href: '/machines' });
        }
        if (showRapier) {
          result.push({ ...module, name: showWater ? 'Rapier Jacket' : module.name, href: '/rapier-machine', icon: Factory });
        }
        // Fallback: if neither flag is set, show default machines entry
        if (!showWater && !showRapier) {
          result.push(module);
        }
        continue;
      }

      // Process other modules with submenus as before
      if (module.module === 'production') {
        result.push({
          ...module,
          children: [
            { name: 'Meter Entries', href: '/meter-entries', icon: ClipboardList, module: 'production' }
          ]
        });
        continue;
      }
      if (module.module === 'salary') {
        result.push({
          ...module,
          children: [
            { name: 'Employee Report', href: '/employee-report', icon: Calendar, module: 'salary' }
          ]
        });
        continue;
      }
      if (INVENTORY_MODULES.has(module.module)) {
        if (!inventoryBuilt) { buildInventory(); inventoryBuilt = true; }
        continue;
      }
      if (module.module === 'gps-management') {
        // Add tracker submenu items
        const trackerChildren = trackers.map((tracker: any) => ({
          name: tracker.trackerName || tracker.name || tracker.terminalId,
          href: `/gps-management?tracker=${tracker.terminalId}`,
          icon: MapPin,
          module: 'gps-management'
        }));
        result.push({
          ...module,
          children: trackerChildren.length > 0 ? trackerChildren : undefined
        });
        continue;
      }
      if (module.module === 'beams') {
        // Skip beams - Beam Master is under Stock
        continue;
      }
      if (module.module === 'employees') {
        result.push({
          ...module,
          children: [
            { name: 'Attendance', href: '/attendance', icon: UserCheck, module: 'employees' }
          ]
        });
        continue;
      }
      result.push(module);
    }

    // A group whose href merely proxies to its only child has no page of its own, so
    // expanding it just to reach one item is a wasted step — promote that child to the
    // top level. Groups that own a distinct page (Production, Salary, Employees, GPS)
    // keep their submenu, otherwise flattening would hide the parent's own page.
    // The group's `module` is retained so the entry holds its slot in sortModules().
    return result.map(item =>
      item.children?.length === 1 && item.href === item.children[0].href
        ? { ...item.children[0], module: item.module }
        : item
    );
  };

  // Get accessible modules based on user role and permissions
  const getAccessibleModules = (): NavigationItem[] => {
    if (!user) return [];

    const settingsModule = { name: 'Settings', href: '/settings', icon: Settings, module: 'settings' };

    // Helper: check if user has machines access
    const hasMachineAccess = (modules: NavigationItem[]) =>
      modules.some(m => m.module === 'machines');

    // Super admin gets Admin and all other modules
    if (user.role === 'SUPERADMIN') {
      const dashboardModule = { name: t('navigation.dashboard'), href: '/dashboard', icon: BarChart3, module: 'dashboard' };
      const adminModule = { name: t('navigation.admin'), href: '/company-management', icon: Shield, module: 'company-management' };

      // Add all other accessible modules for super admin (minus the DB 'dashboard'
      // module — Dashboard is already added above as a hard-coded top-level entry, so
      // skipping it here avoids a duplicate Dashboard in the sidebar).
      const otherModules = getOtherAccessibleModules().filter(m => m.module !== 'dashboard');

      // Enhance modules with submenus
      const enhancedModules = enhanceModulesWithSubmenus(otherModules);

      // Combine and sort: Dashboard first, Admin, then Machines, GPS, rest alphabetically
      const allModules = [dashboardModule, adminModule, ...enhancedModules];
      if (hasMachineAccess(otherModules)) allModules.push(settingsModule);
      return sortModules(allModules);
    }

    // Admin gets other accessible modules
    if (user.role === 'ADMIN') {
      const dashboardModule = { name: t('navigation.dashboard'), href: '/dashboard', icon: BarChart3, module: 'dashboard' };

      // Add other accessible modules (minus the DB 'dashboard' module — Dashboard is
      // already added above as a hard-coded top-level entry, so skip it to avoid a duplicate).
      const otherModules = getOtherAccessibleModules().filter(m => m.module !== 'dashboard');

      // Enhance modules with submenus
      const enhancedModules = enhanceModulesWithSubmenus(otherModules);

      // Combine and sort: Dashboard first, Machines, then GPS, rest alphabetically
      const allModules = [dashboardModule, ...enhancedModules];
      if (hasMachineAccess(otherModules)) allModules.push(settingsModule);
      return sortModules(allModules);
    }

    // For employees, use the helper function with enhancements
    const otherModules = getOtherAccessibleModules();
    const enhancedModules = enhanceModulesWithSubmenus(otherModules);

    // Sort: Dashboard first (if present), then Machines, GPS, rest alphabetically
    return sortModules(enhancedModules);
  };

  // Attendance nav — self-service clock-in for everyone with an employee profile,
  // plus an Approvals link for anyone allowed to approve attendance.
  const getAttendanceNav = (): NavigationItem[] => {
    if (!user) return [];
    const items: NavigationItem[] = [];
    // Self-service clock-in: open to any user with an employee profile (ungated)
    if (user.employee?.id) {
      items.push({ name: 'My Attendance', href: '/my-attendance', icon: UserCheck, module: 'my-attendance' });
    }
    // Approvals: shown to fixed admins and to employees flagged "Can approve attendance".
    // The flag alone is sufficient — no separate 'attendance' module permission required.
    const canApprove =
      user.role === 'SUPERADMIN' || user.role === 'ADMIN' || !!user.employee?.canApproveAttendance;
    const isAdmin = user.role === 'SUPERADMIN' || user.role === 'ADMIN';
    if (canApprove) {
      // The Approvals / Salary / People sections live on one page but are exposed as a
      // collapsible submenu (like Inventory). Salary & People are admin-only, matching
      // the page's own adminOnly gating.
      const children: NavigationItem[] = [
        { name: 'Approvals', href: '/attendance-approvals', icon: ClipboardList, module: 'attendance' },
      ];
      if (isAdmin) {
        children.push({ name: 'Salary', href: '/attendance-approvals/salary', icon: DollarSign, module: 'attendance' });
        children.push({ name: 'People', href: '/attendance-approvals/people', icon: Users, module: 'attendance' });
      }
      items.push({
        name: 'Attendance Approvals',
        href: '/attendance-approvals',
        icon: ClipboardList,
        module: 'attendance',
        // Only surface the submenu when there is more than one section to reach.
        children: children.length > 1 ? children : undefined,
      });
    }
    return items;
  };

  // Machine-viewer logins get a single-item sidebar: only the Machines page. This
  // hides Dashboard, Attendance Approvals and every other module for them.
  // Otherwise: accessible modules (minus the generic 'attendance' item, which is
  // rendered via getAttendanceNav) plus the attendance nav.
  const navigation = isMachineViewer(user)
    ? [{ name: t('navigation.machines'), href: '/machines', icon: Factory, module: 'machines' }]
    : [
        ...getAccessibleModules().filter((m) => m.module !== 'attendance'),
        ...getAttendanceNav()
      ];

  // Mobile bottom nav: the first few flat (leaf) destinations + a "More" tab that opens the full
  // drawer. Groups are flattened to their children so every tab navigates straight to a page.
  const bottomNav: NavigationItem[] = navigation
    .flatMap((it) => (it.children?.length ? it.children : [it]))
    .slice(0, 4);
  // Short labels so long names don't truncate in the bar; falls back to the full name.
  const bottomLabel = (name: string): string => (({
    'Taka Inventory': 'Taka', 'Yarn Inward': 'Inward', 'Yarn Outward': 'Outward',
    'Yarn Dashboard': 'Yarn', 'Beam Master': 'Beam', 'Attendance Approvals': 'Approvals',
    'My Attendance': 'Attendance', 'Company Management': 'Admin',
  } as Record<string, string>)[name]) || name;
  // Column count = leaf destinations + the "More" tab, so the bar fills its width and
  // labels get maximum room instead of being squeezed into a fixed 5-column grid.
  const bottomCols = bottomNav.length + 1;

  // Debug final navigation
  useEffect(() => {
    if (user?.role === 'SUPERADMIN') {
      console.log('Final navigation for SUPERADMIN:', navigation);
      console.log('Navigation length:', navigation.length);
    }
  }, [navigation, user?.role]);
  

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    // The language change will automatically trigger re-renders through useTranslation hook
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleProfileClick = () => {
    setProfileDropdownOpen(!profileDropdownOpen);
  };

  const handleProfileNavigation = () => {
    navigate('/profile');
    setProfileDropdownOpen(false);
  };

  const toggleSidebarCollapse = () => {
    const next = !sidebarCollapsed;
    setSidebarCollapsed(next);
    localStorage.setItem('sidebarCollapsed', String(next));
  };

  const toggleMenuExpansion = (menuName: string) => {
    setExpandedMenus(prev => {
      const newSet = new Set(prev);
      if (newSet.has(menuName)) {
        newSet.delete(menuName);
      } else {
        newSet.add(menuName);
      }
      return newSet;
    });
  };

  // Get current page title based on route
  const getCurrentPageTitle = () => {
    const path = location.pathname;

    // Define page titles mapping
    const pageTitles: Record<string, string> = {
      '/dashboard': t('navigation.dashboard'),
      '/profile': t('settings.title'),
      '/company-management': t('navigation.admin'),
      '/role-management': t('navigation.roleManagement'),
      '/chip-management': t('navigation.chipManagement'),
      '/gps-management': t('navigation.gpsManagement'),
      '/yarns': t('navigation.yarns'),
      '/machines': t('navigation.machines'),
      '/rapier-machine': 'Rapier Machines',
      '/employees': t('navigation.employees'),
      '/qualities': t('navigation.qualities'),
      '/purchases': t('navigation.purchases'),
      '/clients': t('navigation.clients'),
      '/sales': t('navigation.sales'),
      '/beams': t('navigation.beams'),
      '/production': t('navigation.production'),
      '/meter-entries': 'Meter Entries',
      '/salary': t('navigation.salary'),
      '/employee-report': 'Employee Report',
      '/stock': t('navigation.stock'),
      '/taka-inventory': 'Taka Inventory',
      '/challan': 'Challan',
      '/beam-master': 'Beam Master',
      '/yarn-master': 'Yarn Inward',
      '/yarn-dashboard': 'Yarn Dashboard',
      '/yarn-outward': 'Yarn Outward',
      '/bill': 'Bill',
      "/cost-calculating": t('costCalculating.title'),
      '/my-attendance': 'My Attendance',
      '/attendance-approvals': 'Attendance Approvals',
    };

    if (pageTitles[path]) return pageTitles[path];
    // Attendance Approvals sub-routes (/salary, /people) share the parent's breadcrumb.
    if (path.startsWith('/attendance-approvals')) return 'Attendance Approvals';
    return t('navigation.dashboard');
  };


  // Sidebar nav item renderer — white sidebar, Figma style
  const renderNavItem = (item: NavigationItem, mobile: boolean) => {
    const isActive = location.pathname === item.href || (item.children && item.children.some(child => location.pathname === child.href));
    const isDirectActive = location.pathname === item.href;
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedMenus.has(item.name);

    // Active style: blue pill with rounded right only, shadow
    const activeClass = 'bg-[#0055a9] text-white shadow-[0_4px_8px_3px_rgba(0,0,0,0.15),0_1px_3px_rgba(0,0,0,0.3)]';
    const inactiveClass = 'text-[#1B202A] hover:bg-black/[0.06]';

    return (
      <div key={item.name}>
        <div className={`flex items-center ${sidebarCollapsed && !mobile ? 'justify-center' : ''}`}>
          {sidebarCollapsed && !mobile ? (
            <Tooltip content={item.name} position="right">
              <button
                onClick={() => navigate(item.href)}
                className={`flex items-center justify-center p-2.5 rounded-r-xl transition-all ${isActive ? activeClass : inactiveClass}`}
              >
                <item.icon className="h-5 w-5" />
              </button>
            </Tooltip>
          ) : (
            <>
              <button
                onClick={(e) => {
                  if (hasChildren) {
                    e.preventDefault();
                    toggleMenuExpansion(item.name);
                    if (!isActive) navigate(item.href);
                  } else {
                    navigate(item.href);
                    if (mobile) setSidebarOpen(false);
                  }
                }}
                className={`group flex flex-1 items-center px-6 py-3 text-[16px] font-medium rounded-r-xl transition-all ${isDirectActive
                  ? activeClass
                  : isActive && hasChildren
                    ? 'text-[#1B202A] bg-black/[0.06]'
                    : inactiveClass
                }`}
              >
                <item.icon className="mr-4 h-5 w-5 flex-shrink-0" />
                {item.name}
              </button>
              {hasChildren && (
                <button
                  onClick={(e) => { e.stopPropagation(); toggleMenuExpansion(item.name); }}
                  className="px-3 py-3 text-[#1B202A]/50 hover:text-[#1B202A] transition-colors"
                >
                  <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                </button>
              )}
            </>
          )}
        </div>
        {hasChildren && (!sidebarCollapsed || mobile) && item.children && isExpanded && (
          <div className="ml-10 space-y-0.5 mt-0.5 mb-1">
            {item.children.map((child: NavigationItem) => {
              const isChildActive = location.pathname === child.href;
              return (
                <button
                  key={child.name}
                  onClick={() => { navigate(child.href); if (mobile) setSidebarOpen(false); }}
                  className={`group text-left flex items-center w-full px-4 py-2 text-[14px] font-medium rounded-r-xl transition-all ${isChildActive
                    ? activeClass
                    : 'text-[#1B202A]/70 hover:bg-black/[0.04] hover:text-[#1B202A]'
                  }`}
                >
                  <child.icon className="mr-3 h-4 w-4 flex-shrink-0" />
                  {child.name}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Language selector — Figma style: light gray pill
  const renderLanguageSelector = (collapsed: boolean) => {
    if (collapsed) return null;
    return (
      <div className="px-3 pb-4">
        <div className="relative flex items-center bg-[#F2F2F7] border border-[#E8EBF1] rounded-xl px-3 py-2.5 cursor-pointer">
          <span className="text-sm font-bold text-[#1B202A] flex-1">
            {i18n.language === 'en' ? 'English' : i18n.language === 'hi' ? 'हिन्दी' : 'ગુજરાતી'}
          </span>
          <ChevronDown className="h-5 w-5 text-[#1B202A]" />
          <select
            value={i18n.language}
            onChange={(e) => changeLanguage(e.target.value)}
            className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
          >
            <option value="en">English</option>
            <option value="hi">हिन्दी</option>
            <option value="gu">ગુજરાતી</option>
          </select>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#FCFCFE]">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-[1000] lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-black/40" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-[280px] flex-col bg-white shadow-2xl">
          {/* Header — Company name in Teko font */}
          <div className="flex items-center px-6 py-4 gap-4 h-[76px]">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <Package className="h-5 w-5 text-[#1B202A]" />
            </div>
            <h1 className="font-display text-[32px] font-bold text-[#0055a9] leading-none truncate" style={{ lineHeight: '1' }}>
              {user?.companyName || 'VueNexa Enterprise'}
            </h1>
          </div>

          {/* Nav + company switchers share one scroll area so a long company list never
              squeezes the nav off-screen on short phones; only Logout stays pinned. */}
          <div className="flex-1 overflow-y-auto scrollbar-thin-light">
            <nav>
              <div className="flex flex-col">
                {navigation.map((item) => renderNavItem(item, true))}
              </div>
            </nav>
            {/* Reseller "view company" switcher — mobile counterpart to the header version. */}
            <ResellerCompanySwitcher variant="drawer" />
            {/* Filter the merged "All my companies" view. Renders nothing unless active. */}
            <ResellerCompanyFilter variant="drawer" />
            {/* SUPERADMIN "all companies vs one company" switcher — SUPERADMIN only. */}
            <SuperAdminCompanySwitcher variant="drawer" />
          </div>

          {/* Logout */}
          <div className="px-3 pb-4">
            <button onClick={handleLogout}
              className="flex items-center gap-3 w-full px-3 py-2.5 text-sm font-semibold text-[#E02D3C] bg-transparent border border-[#E02D3C] rounded-xl hover:bg-red-50 transition-colors">
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className={`hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col lg:z-[1000] transition-all duration-200 ${sidebarCollapsed ? 'lg:w-16' : 'lg:w-[280px]'}`}>
        <div className="flex flex-col flex-grow bg-white">
          {/* Header — Company name */}
          <div className={`flex items-center h-[76px] ${sidebarCollapsed ? 'justify-center px-2' : 'px-6 gap-4'}`}>
            {!sidebarCollapsed ? (
              <>
                <button
                  onClick={toggleSidebarCollapse}
                  className="p-1.5 rounded-xl hover:bg-gray-100 transition-colors flex-shrink-0"
                  title="Collapse sidebar"
                >
                  <Menu className="h-5 w-5 text-[#1B202A]" />
                </button>
                <h1 className="font-display text-[32px] font-bold text-[#0055a9] leading-none truncate" style={{ lineHeight: '1' }}>
                  {user?.companyName || 'VueNexa Enterprise'}
                </h1>
              </>
            ) : (
              <button
                onClick={toggleSidebarCollapse}
                className="p-2 rounded-xl hover:bg-gray-100 transition-colors"
                title="Expand sidebar"
              >
                <Menu className="h-5 w-5 text-[#1B202A]" />
              </button>
            )}
          </div>

          {/* Nav */}
          <nav className="flex-1 overflow-y-auto scrollbar-thin-light">
            <div className="flex flex-col">
              {user?.role === 'SUPERADMIN' && modulesLoading && (
                <div className="px-6 py-3 text-sm text-gray-400">Loading...</div>
              )}
              {navigation.map((item) => renderNavItem(item, false))}
            </div>
          </nav>

          {/* Logout */}
          <div className={`pb-4 ${sidebarCollapsed ? 'px-2' : 'px-3'}`}>
            <button onClick={handleLogout}
              className={`flex items-center w-full text-sm font-semibold text-[#E02D3C] bg-transparent border border-[#E02D3C] rounded-xl hover:bg-red-50 transition-colors ${sidebarCollapsed ? 'justify-center p-2.5' : 'gap-3 px-3 py-2.5'}`}
              title="Logout">
              <LogOut className="w-4 h-4 flex-shrink-0" />
              {!sidebarCollapsed && 'Logout'}
            </button>
          </div>
        </div>
      </div>

      {/* Main content (pb clears the mobile bottom nav bar) */}
      <div className={`transition-all duration-200 pb-20 lg:pb-0 ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-[280px]'}`}>
        {/* Top bar — Figma style */}
        <div className="sticky top-0 z-[1001] flex items-center justify-between gap-2 h-[76px] bg-white px-4 lg:px-4">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {!machineViewer && (
              <button
                type="button"
                className="p-2 text-[#1B202A] lg:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-5 w-5" />
              </button>
            )}

            {/* Back button */}
            {!machineViewer && (
              <button
                type="button"
                onClick={() => navigate(-1)}
                title="Back"
                className="flex items-center justify-center w-11 h-11 bg-[#F2F2F7] border border-[#E8EBF1] rounded-xl hover:bg-[#EAEBF0] transition-colors"
              >
                <svg className="w-5 h-5 text-[#0055a9]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}

            {/* Breadcrumb pill — stays on one line; truncates long titles on small screens */}
            <div className="inline-flex items-center min-w-0 px-3 sm:px-4 py-2.5 sm:py-3 bg-[#F2F2F7] border border-[#E8EBF1] rounded-xl">
              <span className="text-sm font-medium text-[#0055a9] whitespace-nowrap truncate max-w-[40vw] sm:max-w-none">{getCurrentPageTitle()}</span>
            </div>
          </div>

          {/* Logo - Center */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none hidden md:block">
            <div style={{ display: 'flex', alignItems: 'center', gap: '13px' }} role="img" aria-label="VueNexa Enterprise">
              <svg width="40" height="40" viewBox="0 0 64 64" fill="none">
                <rect x="3" y="8" width="8" height="24" rx="4" fill="#00a5bd" />
                <rect x="15.5" y="8" width="8" height="36" rx="4" fill="#008abb" />
                <rect x="28" y="8" width="8" height="48" rx="4" fill="#0055a9" />
                <rect x="40.5" y="8" width="8" height="36" rx="4" fill="#008abb" />
                <rect x="53" y="8" width="8" height="24" rx="4" fill="#00a5bd" />
              </svg>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '9px' }}>
                <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '28px', fontWeight: 600, letterSpacing: '-0.03em', color: '#141b24' }}>VueNexa</span>
                <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '11px', fontWeight: 400, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#0055a9' }}>Enterprise</span>
              </div>
            </div>
          </div>

          {/* Right side: user */}
          <div className="flex items-center gap-3">
            {/* Company switcher for multi-company logins — scopes the Machines page */}
            <MachineCompanySwitcher />
            {/* Reseller "view company" switcher — scopes every page. Hidden below sm:
                there's no room for it alongside hamburger/back/breadcrumb/profile on a
                phone header; mobile gets the same switcher inside the drawer instead. */}
            <div className="hidden sm:block">
              <ResellerCompanySwitcher />
            </div>
            {/* Filter the reseller's merged "All my companies" view down to one company. */}
            <div className="hidden sm:block">
              <ResellerCompanyFilter />
            </div>
            <div className="hidden sm:block">
              <SuperAdminCompanySwitcher />
            </div>
            <ConnectionStatus />

            {/* User profile */}
            <div className="relative" ref={profileDropdownRef}>
              <button
                onClick={handleProfileClick}
                className="flex items-center gap-2 bg-[#F2F2F7] border border-[#E8EBF1] rounded-xl px-0 py-0 h-11 overflow-hidden hover:bg-[#EAEBF0] transition-colors"
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-200 to-amber-400 flex items-center justify-center flex-shrink-0">
                  <span className="text-base font-bold text-[#1B202A]">{user?.name?.charAt(0)?.toUpperCase()}</span>
                </div>
                <div className="text-left hidden sm:block pr-1">
                  <p className="text-sm font-bold text-[#1B202A] truncate max-w-[8rem] leading-tight" style={{ marginTop: '-2px' }}>{user?.name}</p>
                  <p className="text-sm text-[#404040] capitalize leading-tight">{user?.role?.toLowerCase()}</p>
                </div>
                <div className="pr-3 pl-1">
                  <ChevronDown className={`h-5 w-5 text-[#1B202A] transition-transform ${profileDropdownOpen ? 'rotate-180' : ''}`} />
                </div>
              </button>

              {profileDropdownOpen && (
                <div className="absolute right-0 mt-2 w-[222px] bg-white rounded-xl shadow-[0_7px_29px_rgba(100,100,111,0.2)] border border-[#E1E6EF] py-1 z-[1002]">
                  <button
                    onClick={handleProfileNavigation}
                    className="flex items-center w-full px-4 py-2.5 text-sm text-[#0E131B] hover:bg-gray-50 transition-colors"
                  >
                    <User className="h-5 w-5 mr-3 text-[#0E131B]" />
                    Profile
                  </button>
                  <hr className="border-[#E1E6EF]" />
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-2.5 text-sm text-[#E02D3C] hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="h-5 w-5 mr-3" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className={isMachinesPage ? '' : 'p-4 lg:p-5'}>
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom navigation — top destinations + More (opens the full menu) */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-[900] bg-white/95 backdrop-blur border-t border-[#EEF0F5] shadow-[0_-4px_20px_rgba(16,24,40,0.06)]" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="grid px-1 pt-1.5 pb-1" style={{ gridTemplateColumns: `repeat(${bottomCols}, minmax(0, 1fr))` }}>
          {bottomNav.map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.href;
            return (
              <button key={item.href} type="button" onClick={() => navigate(item.href)}
                aria-current={active ? 'page' : undefined}
                className="flex flex-col items-center justify-center gap-1 py-1.5 min-h-[52px] select-none transition-opacity active:opacity-60">
                <Icon className={`w-[23px] h-[23px] transition-colors ${active ? 'text-[#0055a9]' : 'text-[rgba(14,19,27,0.4)]'}`} strokeWidth={active ? 2.3 : 1.9} />
                <span className={`w-full truncate text-center px-0.5 text-[11px] tracking-[-0.01em] leading-tight transition-colors ${active ? 'font-bold text-[#0055a9]' : 'font-medium text-[rgba(14,19,27,0.5)]'}`}>{bottomLabel(item.name)}</span>
              </button>
            );
          })}
          <button type="button" onClick={() => setSidebarOpen(true)}
            className="flex flex-col items-center justify-center gap-1 py-1.5 min-h-[52px] select-none transition-opacity active:opacity-60">
            <Menu className="w-[23px] h-[23px] text-[rgba(14,19,27,0.4)]" strokeWidth={1.9} />
            <span className="text-[11px] font-medium tracking-[-0.01em] leading-tight text-[rgba(14,19,27,0.5)]">More</span>
          </button>
        </div>
      </nav>
    </div>
  );
};

export default Layout;
